class VideoGallery {
  constructor() {
    this.modal = null;
    this.videoContainer = null;
    this.closeButton = null;
    this.overlay = null;
    this.swiperInstances = new Map();
    
    this.init();
  }
  
  init() {
    console.log('VideoGallery initializing...');
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        console.log('DOM loaded, setting up VideoGallery');
        this.setupModal();
        this.initVideoGalleries();
        this.addEventListeners();
      });
    } else {
      console.log('DOM already loaded, setting up VideoGallery');
      this.setupModal();
      this.initVideoGalleries();
      this.addEventListeners();
    }
  }
  
  setupModal() {
    console.log('Setting up modal...');
    // Get modal elements
    this.modal = document.getElementById('video-modal');
    if (this.modal) {
      this.videoContainer = this.modal.querySelector('#video-modal-video');
      this.closeButton = this.modal.querySelector('.video-modal__close');
      this.overlay = this.modal.querySelector('.video-modal__overlay');
      console.log('Modal elements found:', { 
        modal: !!this.modal, 
        videoContainer: !!this.videoContainer,
        closeButton: !!this.closeButton,
        overlay: !!this.overlay
      });
      console.log('Video container element:', this.videoContainer);
    } else {
      console.warn('Video modal not found in DOM');
    }
  }
  
  initVideoGalleries() {
    console.log('Initializing video galleries...');
    const galleries = document.querySelectorAll('.video-gallery__swiper');
    console.log('Found galleries:', galleries.length);
    
    galleries.forEach((gallery, index) => {
      console.log(`Initializing gallery ${index}:`, gallery);
      // Check if Swiper is available
      if (typeof Swiper === 'undefined') {
        console.warn('Swiper not available, skipping gallery initialization');
        this.initVideosWithoutSwiper(gallery);
        return;
      }
      
      // Parse swiper options safely
      let swiperOptions = {};
      try {
        if (gallery.dataset.swiperOptions) {
          swiperOptions = JSON.parse(gallery.dataset.swiperOptions);
        }
      } catch (e) {
        console.error('Failed to parse swiper options:', e);
        // Use default options if parsing fails
        swiperOptions = {
          slidesPerView: 3,
          spaceBetween: 16,
          loop: false,
          navigation: {
            nextEl: '.video-gallery__button--next',
            prevEl: '.video-gallery__button--prev'
          },
          breakpoints: {
            320: {
              slidesPerView: 3,
              spaceBetween: 12
            },
            768: {
              slidesPerView: 3,
              spaceBetween: 12
            },
            1024: {
              slidesPerView: 3,
              spaceBetween: 16
            }
          }
        };
      }
      
      // Initialize Swiper
      try {
        const swiper = new Swiper(gallery, {
          ...swiperOptions,
          allowTouchMove: true,
          simulateTouch: true,
          touchRatio: 1,
          touchAngle: 45,
          shortSwipes: true,
          longSwipes: true,
          followFinger: true,
          threshold: 5,
          on: {
            init: (swiper) => {
              console.log('Swiper initialized');
              this.handleSwiperInit(swiper);
            },
            slideChange: (swiper) => {
              this.handleSlideChange(swiper);
            },
            click: (swiper, event) => {
              console.log('Swiper click event:', event);
              // Let the click propagate to document level
            },
            tap: (swiper, event) => {
              console.log('Swiper tap event:', event);
              // Let the tap propagate to document level
            }
          }
        });
        
        this.swiperInstances.set(gallery, swiper);
      } catch (e) {
        console.error('Failed to initialize Swiper:', e);
        this.initVideosWithoutSwiper(gallery);
      }
    });
  }
  
  initVideosWithoutSwiper(gallery) {
    // Initialize videos without Swiper
    const videos = gallery.querySelectorAll('.video-gallery__video');
    videos.forEach((video, index) => {
      video.autoplay = true;
      video.loop = true;
      video.muted = true;
      video.playsInline = true;
      
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch(e => {
          console.log(`Video ${index} autoplay prevented:`, e.message);
        });
      }
      
      video.addEventListener('ended', () => {
        video.currentTime = 0;
        video.play().catch(e => console.log('Video restart prevented:', e.message));
      });
    });
  }
  
  handleSwiperInit(swiper) {
    // Get all videos in the swiper
    const videos = swiper.el.querySelectorAll('.video-gallery__video');
    
    videos.forEach((video, index) => {
      // Setup video properties
      video.autoplay = true;
      video.loop = true;
      video.muted = true;
      video.playsInline = true;
      
      // Try to play the video
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch(e => {
          console.log(`Video ${index} autoplay prevented:`, e.message);
        });
      }
      
      // Restart video when it ends
      video.addEventListener('ended', () => {
        video.currentTime = 0;
        video.play().catch(e => console.log('Video restart prevented:', e.message));
      });
    });
  }
  
  handleSlideChange(swiper) {
    const activeSlide = swiper.slides[swiper.activeIndex];
    const activeVideo = activeSlide?.querySelector('.video-gallery__video');
    
    if (activeVideo && activeVideo.paused) {
      activeVideo.play().catch(e => console.log('Video play prevented:', e.message));
    }
  }
  
  addEventListeners() {
    console.log('Adding event listeners for video gallery');
    
    // Test: Check if video containers exist
    const containers = document.querySelectorAll('.video-gallery__video-container');
    console.log('Found video containers:', containers.length);
    containers.forEach((container, index) => {
      console.log(`Container ${index}:`, {
        hasVideoUrl: !!container.dataset.videoUrl,
        videoUrl: container.dataset.videoUrl,
        videoTitle: container.dataset.videoTitle
      });
    });
    
    // Handle clicks on video containers with delegation
    document.addEventListener('click', (e) => {
      console.log('Click event detected on:', e.target, 'classes:', e.target.className);
      
      // Check if click is on video container or any of its children
      const container = e.target.closest('.video-gallery__video-container');
      
      if (container) {
        console.log('Video container clicked!', container);
        
        // Check if this click is on a Swiper navigation button
        const isNavigationClick = e.target.closest('.video-gallery__button');
        if (isNavigationClick) {
          console.log('Click on navigation button, ignoring');
          return;
        }
        
        // Prevent default and stop propagation only if we're handling this click
        e.preventDefault();
        e.stopPropagation();
        
        const videoUrl = container.dataset.videoUrl;
        const videoTitle = container.dataset.videoTitle || '';
        
        console.log('Opening video modal with:', { videoUrl, videoTitle });
        
        if (videoUrl && videoUrl.trim() !== '') {
          this.openModal(videoUrl, videoTitle);
        } else {
          console.warn('No video URL found in dataset, trying video element');
          
          // Try to find video source from the video element inside
          const videoElement = container.querySelector('video');
          if (videoElement) {
            const videoSrc = videoElement.src || videoElement.querySelector('source')?.src;
            console.log('Found video element with src:', videoSrc);
            if (videoSrc) {
              this.openModal(videoSrc, videoTitle);
            } else {
              console.error('Video element found but no source');
            }
          } else {
            console.error('No video element found in container');
          }
        }
      }
    }, true); // Use capture phase to catch events before Swiper
    
    // Close modal events
    if (this.closeButton) {
      this.closeButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.closeModal();
      });
    }
    
    if (this.overlay) {
      this.overlay.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.closeModal();
      });
    }
    
    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal && this.modal.classList.contains('is-active')) {
        e.preventDefault();
        this.closeModal();
      }
    });
  }
  
  openModal(videoUrl, videoTitle) {
    console.log('=== OPENING MODAL ===');
    console.log('Video URL:', videoUrl);
    console.log('Video Title:', videoTitle);
    console.log('Modal element:', this.modal);
    console.log('Video container element:', this.videoContainer);
    
    if (!this.modal || !this.videoContainer) {
      console.error('Modal or video container not found', {
        modal: this.modal,
        videoContainer: this.videoContainer
      });
      this.createFallbackModal(videoUrl, videoTitle);
      return;
    }
    
    // Create video element
    const video = document.createElement('video');
    video.src = videoUrl;
    video.controls = true;
    video.autoplay = true;
    video.muted = false;
    video.loop = false;
    video.playsInline = true;
    video.style.width = '100%';
    video.style.height = 'auto';
    video.style.maxHeight = '80vh';
    
    console.log('Created video element:', video);
    console.log('Video source:', video.src);
    console.log('Video element outerHTML:', video.outerHTML);
    
    // Clear previous content and add new video
    console.log('Video container before clearing:', this.videoContainer.innerHTML);
    this.videoContainer.innerHTML = '';
    console.log('Video container after clearing:', this.videoContainer.innerHTML);
    
    this.videoContainer.appendChild(video);
    console.log('Video container after adding video:', this.videoContainer.innerHTML);
    console.log('Video container children:', this.videoContainer.children);
    
    // Show modal
    this.modal.classList.add('is-active');
    document.body.style.overflow = 'hidden';
    
    console.log('Modal classes after adding is-active:', this.modal.className);
    console.log('Modal display style:', window.getComputedStyle(this.modal).display);
    
    // Try to play the video
    video.play().catch(e => {
      console.log('Modal video autoplay prevented:', e.message);
    });
    
    console.log('=== MODAL OPENED SUCCESSFULLY ===');
  }
  
  createFallbackModal(videoUrl, videoTitle) {
    console.log('Creating fallback modal');
    
    const modal = document.createElement('div');
    modal.className = 'video-modal is-active';
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0,0,0,0.9);
      z-index: 9999;
      display: flex;
      align-items: center;
      justify-content: center;
    `;
    
    const content = document.createElement('div');
    content.style.cssText = `
      position: relative;
      max-width: 90vw;
      max-height: 90vh;
    `;
    
    const closeBtn = document.createElement('button');
    closeBtn.innerHTML = '×';
    closeBtn.style.cssText = `
      position: absolute;
      top: -40px;
      right: 0;
      background: white;
      border: none;
      width: 32px;
      height: 32px;
      font-size: 24px;
      cursor: pointer;
      border-radius: 50%;
      z-index: 10;
    `;
    
    const video = document.createElement('video');
    video.src = videoUrl;
    video.controls = true;
    video.autoplay = true;
    video.muted = false;
    video.style.cssText = `
      width: 100%;
      max-width: 800px;
      height: auto;
      display: block;
    `;
    
    content.appendChild(closeBtn);
    content.appendChild(video);
    modal.appendChild(content);
    document.body.appendChild(modal);
    document.body.style.overflow = 'hidden';
    
    // Play video
    video.play().catch(e => console.log('Fallback video autoplay prevented:', e.message));
    
    // Close handlers
    const closeModal = () => {
      document.body.removeChild(modal);
      document.body.style.overflow = '';
    };
    
    closeBtn.addEventListener('click', closeModal);
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        closeModal();
      }
    });
  }
  
  closeModal() {
    if (!this.modal) return;
    
    // Hide modal
    this.modal.classList.remove('is-active');
    document.body.style.overflow = '';
    
    // Clear video content
    if (this.videoContainer) {
      this.videoContainer.innerHTML = '';
    }
    
    console.log('Modal closed');
  }
}

// Singleton instance
let videoGalleryInstance = null;

// Initialize when DOM is ready
function initVideoGallery() {
  if (videoGalleryInstance) {
    console.log('VideoGallery already initialized, skipping');
    return;
  }
  
  console.log('Initializing VideoGallery');
  videoGalleryInstance = new VideoGallery();
}

// Wait for both DOM and Swiper
function waitForSwiperAndInit() {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      console.log('DOM Content Loaded');
      checkSwiperAndInit();
    });
  } else {
    console.log('DOM already loaded');
    checkSwiperAndInit();
  }
}

function checkSwiperAndInit() {
  if (typeof Swiper !== 'undefined') {
    console.log('Swiper is available, initializing VideoGallery');
    initVideoGallery();
  } else {
    console.log('Waiting for Swiper...');
    let attempts = 0;
    const checkSwiper = setInterval(() => {
      attempts++;
      if (typeof Swiper !== 'undefined') {
        clearInterval(checkSwiper);
        console.log('Swiper loaded after', attempts, 'attempts');
        initVideoGallery();
      } else if (attempts > 30) {
        clearInterval(checkSwiper);
        console.warn('Swiper not found after 3 seconds, initializing without it');
        initVideoGallery();
      }
    }, 100);
  }
}

// Start initialization
waitForSwiperAndInit();

// Re-initialize for Shopify theme editor
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', function(event) {
    if (event.detail.sectionId && event.target.querySelector('.video-gallery__swiper')) {
      console.log('Shopify section loaded, re-initializing VideoGallery');
      videoGalleryInstance = null; // Reset instance for theme editor
      setTimeout(() => {
        initVideoGallery();
      }, 100);
    }
  });
}

// Global test function for debugging
window.testVideoModal = function() {
  console.log('=== TESTING VIDEO MODAL ===');
  const modal = document.getElementById('video-modal');
  const videoContainer = document.querySelector('#video-modal-video');
  console.log('Modal found:', !!modal);
  console.log('Video container found:', !!videoContainer);
  console.log('Modal element:', modal);
  console.log('Video container element:', videoContainer);
  
  if (modal && videoContainer) {
    // Test with a sample video
    const testVideo = document.createElement('video');
    testVideo.src = 'https://www.w3schools.com/html/mov_bbb.mp4';
    testVideo.controls = true;
    testVideo.style.width = '100%';
    testVideo.style.height = 'auto';
    testVideo.style.maxHeight = '80vh';
    
    console.log('Created test video:', testVideo);
    console.log('Test video src:', testVideo.src);
    
    videoContainer.innerHTML = '';
    videoContainer.appendChild(testVideo);
    
    console.log('Video container after adding test video:', videoContainer.innerHTML);
    
    modal.classList.add('is-active');
    document.body.style.overflow = 'hidden';
    
    console.log('Modal classes:', modal.className);
    console.log('Modal display style:', window.getComputedStyle(modal).display);
    
    console.log('=== TEST MODAL OPENED ===');
  } else {
    console.error('Modal or video container not found');
    console.log('Available elements with video-modal class:', document.querySelectorAll('.video-modal'));
    console.log('Available elements with video-modal-video id:', document.querySelectorAll('#video-modal-video'));
  }
};

// Test function to check if elements exist
window.checkModalElements = function() {
  console.log('=== CHECKING MODAL ELEMENTS ===');
  console.log('All elements with video-modal class:', document.querySelectorAll('.video-modal'));
  console.log('Element with video-modal id:', document.getElementById('video-modal'));
  console.log('All elements with video-modal-video id:', document.querySelectorAll('#video-modal-video'));
  console.log('All elements with video-modal__video class:', document.querySelectorAll('.video-modal__video'));
};